import type { ShellMessage } from '@harleys/shared-types';
import { isInShell, sendToShell, onShellMessage } from './messaging';

interface CachedToken {
  token: string;
  expiresAt: number;
}

let cached: CachedToken | null = null;
let pendingResolvers: Array<(token: string) => void> = [];
let pendingRejecters: Array<(err: Error) => void> = [];

export function initShellAuth(): void {
  if (!isInShell()) return;

  onShellMessage((msg: ShellMessage) => {
    if (msg.type === 'shell:token') {
      cached = { token: msg.token, expiresAt: msg.expiresAt };
      pendingResolvers.forEach(r => r(msg.token));
      pendingResolvers = [];
      pendingRejecters = [];
    }
  });
}

export async function getAccessToken(): Promise<string> {
  if (cached && Date.now() < cached.expiresAt - 60_000) {
    return cached.token;
  }

  if (!isInShell()) {
    throw new Error(
      'Mini-app is not running inside the Pipeline shell and no fallback auth provider is configured. ' +
      'Use getAccessTokenWithFallback() to provide an MSAL fallback for development.'
    );
  }

  return new Promise<string>((resolve, reject) => {
    pendingResolvers.push(resolve);
    pendingRejecters.push(reject);
    sendToShell({ type: 'app:request-token' });
    setTimeout(() => {
      const idx = pendingResolvers.indexOf(resolve);
      if (idx >= 0) {
        pendingResolvers.splice(idx, 1);
        pendingRejecters.splice(idx, 1);
        reject(new Error('Shell did not provide a token within 10 seconds'));
      }
    }, 10_000);
  });
}

export async function getAccessTokenWithFallback(
  fallback: () => Promise<{ token: string; expiresAt: number }>
): Promise<string> {
  if (isInShell()) return getAccessToken();
  if (cached && Date.now() < cached.expiresAt - 60_000) return cached.token;
  const result = await fallback();
  cached = result;
  return result.token;
}

export function clearTokenCache(): void {
  cached = null;
}
